"use client";

/**
 * SignalHero — top metric strip.
 *
 * Headline is whole-book truth: "DEPLOYED $X → NOW WORTH $X+pnl" where
 * deployed covers ALL trades (open + closed) and the PnL is live
 * (realized on closed + unrealized marks on open). Falls back to the old
 * $10k framing only if the backend doesn't send deployed_usd.
 *
 * Styling contract: white text only, emerald accent, minimal rounding.
 */

import { TrendingUp, TrendingDown } from "lucide-react";
import type {
  SignalPerformanceSummary,
  CurrentPosition,
} from "@/lib/mock/signal-performance";

function pct(n: number) {
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toFixed(1)}%`;
}

function usd(n: number) {
  return `$${Math.round(n).toLocaleString()}`;
}

function Stat({
  label,
  value,
  positive,
}: {
  label: string;
  value: string;
  positive?: boolean;
}) {
  const color =
    positive === undefined
      ? "text-white"
      : positive
        ? "text-emerald-400"
        : "text-red-400";
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-bold uppercase tracking-widest text-white/70">
        {label}
      </span>
      <span className={`text-sm font-bold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}

export function SignalHero({
  summary,
  current,
}: {
  summary: SignalPerformanceSummary;
  current: CurrentPosition;
}) {
  const deployed = summary.deployed_usd ?? 0;
  const bookPnl = summary.total_pnl_usd ?? 0;
  const hasBook = deployed > 0;

  const headlineLabel = hasBook
    ? `${usd(deployed)} deployed is now`
    : "$10,000 invested is now";
  const headlineValue = hasBook
    ? deployed + bookPnl
    : 10000 + summary.all_time_usd_on_10k;
  const headlinePct = hasBook
    ? (bookPnl / deployed) * 100
    : summary.all_time_pct;
  const up = headlinePct >= 0;

  const tradesValue =
    summary.open_trades != null && summary.open_trades > 0
      ? `${summary.total_trades} (${summary.open_trades} active)`
      : `${summary.total_trades}`;

  const firedAgo = timeAgo(current.fired_at);

  return (
    <div className="relative overflow-hidden rounded-sm border border-white/10 bg-black/40 p-4 sm:p-5">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-emerald-400/[0.07] blur-3xl"
      />
      <div className="relative flex flex-wrap items-end justify-between gap-6">
        {/* Headline: live book value on total deployed */}
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/70">
            {headlineLabel}
          </span>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-3xl sm:text-4xl font-extrabold text-white tabular-nums tracking-tight leading-none">
              {usd(headlineValue)}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[13px] font-bold tabular-nums translate-y-[-1px] ${
                up
                  ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-400"
                  : "border-red-500/30 bg-red-500/15 text-red-400"
              }`}
            >
              {up ? (
                <TrendingUp className="w-3.5 h-3.5" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5" />
              )}
              {pct(headlinePct)}
            </span>
            {hasBook && (
              <span
                className={`text-sm font-bold tabular-nums ${
                  bookPnl >= 0 ? "text-emerald-400" : "text-red-400"
                }`}
              >
                {bookPnl >= 0 ? "+" : "−"}
                {usd(Math.abs(bookPnl))} profit
              </span>
            )}
          </div>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-3 gap-x-8 gap-y-3 sm:grid-cols-5">
          <Stat label="Win rate" value={`${Math.round(summary.win_rate * 100)}%`} />
          <Stat label="Trades" value={tradesValue} />
          <Stat label="7d" value={pct(summary.pct_7d)} positive={summary.pct_7d >= 0} />
          <Stat label="30d" value={pct(summary.pct_30d)} positive={summary.pct_30d >= 0} />
          <Stat label="Last fired" value={firedAgo} />
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}
